import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Form } from '@themesberg/react-bootstrap';
import { useMutation, useNotificationApi } from './context';
import { ErrorNotice, Field } from './ui';
import { stamp } from './model';
import TestRecipients from './TestRecipients';

const sourceLabels = { vietlott: 'Vietlott', classic: 'XSKT', loto: 'Lô tô', account: 'Tài khoản' };
const typeLabels = { cart: 'Đơn hàng', ticket: 'Vé', transaction: 'Giao dịch', payout: 'Trả thưởng', charge: 'Nạp tiền', withdrawal: 'Rút tiền', game_period: 'Kỳ quay' };
export const refPairs = ['ticket:vietlott', 'ticket:classic', 'cart:vietlott', 'cart:classic', 'transaction:vietlott', 'transaction:classic',
  'payout:vietlott', 'payout:classic', 'ticket:loto', 'cart:loto', 'transaction:loto', 'payout:loto', 'charge:account', 'withdrawal:account', 'payout:account', 'game_period:vietlott'];

function useLookup(path, query = {}, enabled = true) {
  const { api } = useNotificationApi();
  const key = JSON.stringify(query);
  const scope = useMemo(() => ({ api, path, key, enabled }), [api, path, key, enabled]);
  const current = useRef(scope); current.current = scope;
  const sequence = useRef(0);
  const [result, setResult] = useState(null);
  const load = useCallback(async (cursor, append = false) => {
    if (!scope.enabled) return;
    const attempt = ++sequence.current;
    setResult(previous => ({ scope, data: append && previous?.scope === scope ? previous.data : null, busy: true, error: null }));
    try {
      const response = await scope.api('GET', '/link-options/' + scope.path, { query: { ...JSON.parse(scope.key), limit: 20, ...(cursor ? { cursor } : {}) } });
      if (current.current !== scope || attempt !== sequence.current) return;
      setResult(previous => ({ scope, busy: false, error: null, data: { ...response.data,
        items: append ? [...(previous?.data?.items || []), ...response.data.items] : response.data.items } }));
    } catch (error) { if (current.current === scope && attempt === sequence.current) setResult({ scope, busy: false, data: null, error }); }
  }, [scope]);
  useEffect(() => {
    const requests = sequence;
    const timer = setTimeout(() => load(), 300);
    return () => { clearTimeout(timer); requests.current++; };
  }, [load]);
  const visible = result?.scope === scope ? result : { busy: enabled, data: null, error: null };
  return { ...visible, refresh: () => load(), more: () => load(visible.data?.nextCursor, true) };
}

function LookupFeedback({ lookup, disabled }) {
  return <><ErrorNotice error={lookup.error} />
    {lookup.busy && <p className="small text-muted" role="status">Đang tải gợi ý…</p>}
    {lookup.error && <Button type="button" size="sm" variant="outline-secondary" disabled={disabled || lookup.busy} onClick={lookup.refresh}>Tải lại gợi ý</Button>}
    {lookup.data?.nextCursor && <Button type="button" size="sm" variant="outline-secondary" disabled={disabled || lookup.busy} onClick={lookup.more}>Xem thêm</Button>}
  </>;
}

export function GameSelect({ value = '', onChange, disabled, label = 'Trò chơi' }) {
  const lookup = useLookup('games');
  const items = lookup.data?.items || [];
  const selected = items.find(row => row.id === value);
  return <Field label={label}>
    <Form.Select aria-label={label} value={value} disabled={disabled} onChange={event => onChange(event.target.value)}>
      <option value="">Chọn trò chơi</option>
      {value && !selected && <option value={value}>Đã chọn: {value}</option>}
      {items.map(row => <option key={row.id} value={row.id}>{row.name}{row.webDirect ? '' : ' · web mở nội dung thông báo'}</option>)}
    </Form.Select>
    {selected?.icon && <img src={selected.icon} height={32} alt={selected.name} className="mt-2" />}
    <LookupFeedback lookup={lookup} disabled={disabled} />
  </Field>;
}

export function PeriodPicker({ gameId, value = '', disabled, onChange, label = 'Kỳ tham chiếu (tùy chọn)' }) {
  const [query, setQuery] = useState('');
  const [lookupId, setLookupId] = useState('');
  useEffect(() => { setQuery(''); setLookupId(''); }, [gameId]);
  const lookup = useLookup('periods', { gameId, ...(lookupId.trim() ? { id: lookupId.trim() } : query.trim() ? { query: query.trim() } : {}) }, !!gameId);
  const exact = useLookup('periods', { gameId, id: value }, !!(gameId && value));
  const selected = exact.data?.items?.find(row => row.id === value);
  return <div className="mb-3">
    <Field label={label} hint="Khách mở thông tin đúng game/kỳ. Chọn vé mới được thực hiện riêng ở trang game.">
      <Form.Control aria-label={label} maxLength={100} value={query} disabled={disabled || !gameId} placeholder="Tìm số kỳ hoặc ngày YYYY-MM-DD" onChange={e => { setQuery(e.target.value); setLookupId(''); }} />
    </Field>
    {value && <Alert variant="light" className="border py-2">Đã chọn: {selected ? `Kỳ #${selected.name} · ${stamp(selected.dialDate)}` : value}
      <Button type="button" variant="link" size="sm" disabled={disabled} onClick={() => onChange(null)}>Bỏ kỳ</Button></Alert>}
    <LookupFeedback lookup={lookup} disabled={disabled} />
    {gameId && lookup.data?.items?.length === 0 && <p className="small text-muted">Không tìm thấy kỳ phù hợp.</p>}
    {lookup.data?.items?.map(row => <div key={row.id} className="d-flex flex-wrap gap-2 justify-content-between border-bottom py-2">
      <span>Kỳ #{row.name} · {stamp(row.dialDate)}</span><Button type="button" size="sm" variant="outline-primary" disabled={disabled} onClick={() => onChange(row)}>Chọn kỳ #{row.name}</Button>
    </div>)}
    <details className="mt-2"><summary className="small">Tra mã kỳ chính xác</summary><Form.Control aria-label="Mã kỳ chính xác" maxLength={128} placeholder="Dán mã để tìm, không tự gắn" disabled={disabled || !gameId} value={lookupId} onChange={e => setLookupId(e.target.value)} /></details>
    <ErrorNotice error={exact.error} />
  </div>;
}

export function GameLinkPicker({ action, onChange, disabled }) {
  return <><GameSelect value={action.gameId} disabled={disabled} onChange={gameId => onChange({ ...action, gameId, periodId: null })} />
    <PeriodPicker gameId={action.gameId} value={action.periodId} disabled={disabled} onChange={row => onChange({ ...action, periodId: row?.id || null })} /></>;
}

export function EntityPicker({ ownerId, disabled, onSelect, onReset, allowReferenceOnly = false, allowCustomerSearch = true }) {
  const { api } = useNotificationApi();
  const mutation = useMutation();
  const clear = mutation.clear;
  const [lookupError, setLookupError] = useState(null);
  const [pair, setPair] = useState('ticket:vietlott'), [query, setQuery] = useState(''), [exactId, setExactId] = useState('');
  const [customer, setCustomer] = useState(null), [gameId, setGameId] = useState(''), [period, setPeriod] = useState(null);
  const [type, family] = pair.split(':');
  const userId = ownerId || customer?.id;
  const scope = useMemo(() => ({ api, pair, userId, gameId }), [api, pair, userId, gameId]);
  const current = useRef(scope); current.current = scope;
  useEffect(() => { setCustomer(null); setQuery(''); setExactId(''); setPeriod(null); }, [api]);
  useEffect(() => { setQuery(''); setExactId(''); setLookupError(null); clear(); }, [pair, userId, clear]);
  const lookup = useLookup('entities', { type, family, userId, ...(query.trim() ? { query: query.trim() } : {}) }, !!userId && type !== 'game_period');
  const choose = async row => {
    const requested = scope;
    const ref = row.ref;
    onReset?.(); setLookupError(null);
    const response = await mutation.run('POST', '/refs/resolve', { body: { ref } });
    if (response && current.current === requested) {
      if (response.data.private && response.data.ownerUserId !== userId) setLookupError(new Error('Đối tượng không thuộc khách đã chọn.'));
      else onSelect(response.data, row);
    }
  };
  const exact = async () => {
    const requested = scope;
    setLookupError(null);
    const response = await mutation.run('GET', '/link-options/entities', { query: { type, family, userId, id: exactId.trim(), limit: 1 } });
    if (current.current !== requested) return;
    if (response?.data?.items?.[0]) choose(response.data.items[0]);
    else if (response) setLookupError(new Error('Không tìm thấy mã thuộc tài khoản đã chọn.'));
  };
  const locked = disabled || mutation.busy;
  return <div className="border rounded p-3 mb-3">
    <Field label="Đối tượng liên kết"><Form.Select aria-label="Đối tượng liên kết" value={pair} disabled={locked} onChange={e => { setPair(e.target.value); onReset?.(); }}>
      {refPairs.filter(value => allowReferenceOnly || value !== 'game_period:vietlott').map(value => <option key={value} value={value}>{typeLabels[value.split(':')[0]]} · {sourceLabels[value.split(':')[1]]}</option>)}
    </Form.Select></Field>
    {type === 'game_period' ? <><GameSelect value={gameId} onChange={value => { setGameId(value); setPeriod(null); onReset?.(); }} disabled={locked} />
      <PeriodPicker gameId={gameId} value={period?.id} disabled={locked} label="Tìm kỳ tham chiếu" onChange={row => {
        setPeriod(row); if (row) choose({ ref: { type, family, id: row.id, label: 'Kỳ #' + row.name } }); else onReset?.();
      }} /></> : <>
      {!ownerId && allowCustomerSearch && <TestRecipients disabled={locked} mode="reference" onSelect={(id, row) => { setCustomer(row); onReset?.(); }} />}
      {userId ? <p className="small text-muted">Chỉ gợi ý dữ liệu của khách: {customer?.name || userId}</p> : <p className="small text-muted">Chọn một khách để tìm đối tượng thuộc đúng tài khoản.</p>}
      <Field label="Tìm theo mã"><Form.Control aria-label="Tìm mã đối tượng" value={query} disabled={locked || !userId} placeholder="Nhập một phần mã vé/đơn/giao dịch" onChange={e => setQuery(e.target.value)} /></Field>
      <LookupFeedback lookup={lookup} disabled={locked} />
      {lookup.data?.items?.length === 0 && <p className="small text-muted">Không có dữ liệu phù hợp của khách này.</p>}
      {lookup.data?.items?.map(row => <div key={row.ref.id} className="border-bottom py-2">
        <strong className="text-break">{row.ref.label}</strong><div className="small text-muted">{row.gameId || sourceLabels[family]}{row.periodName ? ` · Kỳ #${row.periodName}` : ''} · {stamp(row.dialDate || row.createdAt)}</div>
        <div className="small">{row.webDirect ? 'Web mở chi tiết ' + typeLabels[type].toLowerCase() : 'Web hiện mở nội dung thông báo'}</div>
        <Button type="button" size="sm" variant="outline-primary" disabled={locked} onClick={() => choose(row)}>Chọn {row.ref.label}</Button>
      </div>)}
      <details className="mt-3"><summary className="small">Tra mã chính xác</summary><Form.Control aria-label="Mã đối tượng chính xác" value={exactId} disabled={locked || !userId} onChange={e => setExactId(e.target.value)} />
        <Button type="button" size="sm" variant="outline-secondary" disabled={locked || !userId || !exactId.trim()} onClick={exact}>Tìm mã chính xác</Button></details>
    </>}
    <ErrorNotice error={mutation.error} />
    <ErrorNotice error={lookupError} />
  </div>;
}
